/**
 * Entity breakdown for the cloud tabs — AWS accounts or GCP projects.
 *
 * Clicking a row selects it and filters the sibling service table client-side
 * off the period matrix; clicking it again clears the filter. `group` and
 * `featured` are unused by AWS and drive GCP's billing-account grouping and
 * pinned featured projects.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { EmptyState, fmtPct, fmtUSD } from './shared';

export interface EntityRow {
  id: string;
  name: string;
  cost: number;
  /** GCP billing account label; rows are grouped under it when present. */
  group?: string;
  /** GCP hand-picked projects, pinned above the rest. */
  featured?: boolean;
}

export function AccountOrProjectTable({
  title,
  subtitle,
  rows,
  total,
  selectedId,
  onSelect,
  entityColLabel,
  emptyTitle,
  emptyDescription,
}: {
  title: string;
  subtitle?: string;
  rows: EntityRow[];
  total: number;
  selectedId?: string;
  onSelect: (id: string | undefined) => void;
  entityColLabel: string;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle ?? t('finops.cloud.entities_empty_title')}
        description={emptyDescription ?? t('finops.cloud.month_empty_description')}
      />
    );
  }

  // Only render group headers when the caller actually groups (GCP).
  const grouped = rows.some((r) => r.group);
  const groups = grouped
    ? Array.from(new Set(rows.map((r) => r.group ?? ''))).map((g) => ({ group: g, rows: rows.filter((r) => (r.group ?? '') === g) }))
    : [{ group: '', rows }];

  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold mb-3">
        {title}
        {subtitle && <span className="text-muted-foreground font-normal"> · {subtitle}</span>}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3">{entityColLabel}</th>
              <th className="pr-3 text-right">{t('finops.cloud.col_cost')}</th>
              <th className="pr-3 text-right w-20">{t('finops.cloud.col_share')}</th>
              <th className="w-1/4" />
            </tr>
          </thead>
          <tbody>
            {groups.map(({ group, rows: groupRows }) => (
              <Fragment key={group || '__all__'}>
                {group && (
                  <tr className="border-b bg-muted/30">
                    <td colSpan={4} className="py-1.5 pr-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {group}
                    </td>
                  </tr>
                )}
                {groupRows.map((r) => {
                  const share = total > 0 ? (r.cost / total) * 100 : 0;
                  const selected = selectedId === r.id;
                  return (
                    <tr
                      key={`${group}-${r.id}`}
                      onClick={() => onSelect(selected ? undefined : r.id)}
                      className={`border-b last:border-0 cursor-pointer hover:bg-muted/20 ${selected ? 'bg-muted/40' : ''}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5 font-medium">
                          {r.featured && <Star className="h-3 w-3 text-amber-500 shrink-0" />}
                          <span className="truncate">{r.name}</span>
                        </div>
                        {r.name !== r.id && (
                          <div className="text-xs text-muted-foreground font-mono">{r.id}</div>
                        )}
                      </td>
                      <td className="pr-3 text-right tabular-nums">{fmtUSD(r.cost)}</td>
                      <td className="pr-3 text-right tabular-nums text-muted-foreground">{fmtPct(share)}</td>
                      <td className="py-2">
                        <div className="h-2 rounded bg-muted overflow-hidden">
                          <div className="h-full rounded bg-brand-green" style={{ width: `${Math.min(100, share)}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {selectedId && (
        <p className="text-xs text-muted-foreground mt-3">{t('finops.cloud.entity_filter_hint')}</p>
      )}
    </Card>
  );
}
